import { useEffect } from 'react'

import { useStore } from '~/store/store'
import { getDepartmentNameAndSurgeries, getSurgeryMetadataCounts, getUniqueDIPSOrgs, trackUserEvent } from '~/utils/analytics'

import { MultiQueryOptions, useSurgeryMetadataQuery, useUnscheduledSurgeriesQuery } from '..'
import { mergeQueryStatuses } from '../shared/queryHelpers'

/**
 * Fetch all unscheduled surgeries and associated metadata and put them in the store.
 *
 * @param options - (Optional) Parameters for the queries, such as skip and refetchInterval.
 */
export function useUnscheduledSurgeriesAndMetadataQueries(options?: MultiQueryOptions) {
    const unscheduledSurgeriesQuery = useUnscheduledSurgeriesQuery(options)

    const folderIds =
        unscheduledSurgeriesQuery.data
            ?.map(surgery => surgery.surgeryOrderDetails.folderId)
            .filter((folderId): folderId is string => typeof folderId === 'string') ?? []

    const surgeryMetadataQuery = useSurgeryMetadataQuery(folderIds, options)

    const queryResults = mergeQueryStatuses(unscheduledSurgeriesQuery, surgeryMetadataQuery)

    useEffect(() => {
        if (!queryResults.isLoading) {
            const unScheduledSurgeries = useStore.getState().dips.entities.unScheduledSurgeries
            const { departmentName, surgeries } = getDepartmentNameAndSurgeries(unScheduledSurgeries)
            const surgeryMetadataCounts = getSurgeryMetadataCounts(surgeries)

            // Temporary logging for configuring correct DIPS departments
            const orgData = getUniqueDIPSOrgs(surgeries)

            const eventData = {
                departmentName,
                surgeryType: 'unscheduled' as const,
                ...surgeryMetadataCounts,
                orgData,
            }

            void trackUserEvent('SURGERY_METADATA_COUNTED', eventData)
        }
    }, [queryResults.isLoading])

    return queryResults
}
